// Aba "Já passou" do Férias e Licenças (redesenho 27/10/2026).
// Só consulta: tabela compacta, tons apagados, do mais recente para o mais antigo.

import {
  Box, Paper, Typography, Chip, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { RegistroAfast, situacao, dataRetorno, rotuloTipo, fmtCurta, fmtSemLonga } from './afastamentosDatas';
import { ALERTA, CARTAO_SX } from './visual';

interface Props<T extends RegistroAfast> {
  registros: T[];
  hoje: string;
  alertas: Record<number, string[]>;
  onEditar: (r: T) => void;
  onExcluir: (r: T) => void;
  onAbrirAgente?: (agenteId: number) => void;   // abre a Ficha do agente
}

const CAB_SX = { backgroundColor: '#F3F4F6', color: '#4B5563', fontWeight: 700, fontSize: 12, textTransform: 'uppercase' as const, letterSpacing: 0.5 };

export default function TabelaJaPassou<T extends RegistroAfast>({ registros, hoje, alertas, onEditar, onExcluir, onAbrirAgente }: Props<T>) {
  const passados = registros
    .filter(r => situacao(r, hoje) === 'passado')
    .sort((a, b) => (a.data_inicio !== b.data_inicio ? (a.data_inicio > b.data_inicio ? -1 : 1) : b.id - a.id));

  return (
    <Box>
      <Typography sx={{ fontSize: 14, color: '#6B7280', mb: 1 }}>
        Do mais recente para o mais antigo. Só consulta — a equipe já voltou.
      </Typography>
      <TableContainer component={Paper} elevation={0} sx={{ ...CARTAO_SX, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              {['Agente', 'Tipo', 'Período', 'Voltou', 'Motivo', 'Ações'].map(c => (
                <TableCell key={c} sx={CAB_SX} align={c === 'Ações' ? 'right' : 'left'}>{c}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {passados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5, color: '#6B7280' }}>
                  Nenhum afastamento passado com os filtros atuais.
                </TableCell>
              </TableRow>
            ) : passados.map(r => {
              const al = alertas[r.id] || [];
              return (
                <TableRow key={r.id} sx={{ '& td': { color: '#6B7280', verticalAlign: 'top' } }}>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => onAbrirAgente && onAbrirAgente(r.agente_id)}
                      aria-label={`Abrir ficha de ${r.agente_nome}`}
                      sx={{ all: 'unset', cursor: 'pointer', fontWeight: 600, color: '#4B5563',
                        textDecoration: 'underline', textDecorationColor: 'transparent',
                        '&:hover, &:focus-visible': { textDecorationColor: '#4B5563' } }}
                    >
                      {r.agente_nome}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{rotuloTipo(r.tipo)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                    {fmtCurta(r.data_inicio)} → {fmtCurta(r.data_fim)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtSemLonga(dataRetorno(r))}</TableCell>
                  <TableCell sx={{ minWidth: 220, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {r.motivo || '—'}
                    {al.length > 0 && (
                      <Box sx={{ mt: 0.8, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {al.map(a => (
                          <Chip key={a} size="small" icon={<WarningAmberIcon sx={{ color: `${ALERTA.cor} !important`, fontSize: '16px !important' }} />}
                            label={a} sx={{ backgroundColor: ALERTA.bg, color: ALERTA.cor, fontWeight: 700 }} />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Editar" arrow>
                      <IconButton aria-label={`Editar afastamento de ${r.agente_nome}`} onClick={() => onEditar(r)} sx={{ width: 44, height: 44 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir" arrow>
                      <IconButton aria-label={`Excluir afastamento de ${r.agente_nome}`} onClick={() => onExcluir(r)} sx={{ width: 44, height: 44 }}>
                        <DeleteIcon fontSize="small" sx={{ color: '#EF4444' }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
